// LEGAL STATUS: To be legally validated before commercial production (EN translation of fr/PrivacyPage.js).
import { Link } from "react-router-dom";
import { Page, H1, H2, H3, Lead, P, UL, LegalValue, Notice, Toc, PublisherCountry, DocVersion } from "../../public/primitives";
import { legal } from "../../../config/legal";

export const PrivacyPage = () => (
  <Page testid="privacy-page">
    <H1>Privacy policy</H1>
    <Lead>
      How SecurePass processes data depending on how the service is used. Version <DocVersion value={legal.privacyVersion} /> —
      last updated {legal.lastUpdated}.
    </Lead>

    <Toc
      items={[
        { id: "responsable", label: "Data controller" },
        { id: "donnees", label: "Data processed" },
        { id: "finalites", label: "Purposes" },
        { id: "destinataires", label: "Recipients" },
        { id: "sous-traitants", label: "Subprocessors" },
        { id: "conservation", label: "Retention" },
        { id: "securite", label: "Security" },
        { id: "test-mdp", label: "Password test" },
        { id: "droits", label: "Your rights" },
        { id: "exercice", label: "Exercising your rights" },
        { id: "transferts", label: "Transfers" },
        { id: "maj", label: "Updates" },
      ]}
    />

    <H2 id="responsable">Data controller</H2>
    <P>
      The data controller is <b>{legal.publisherName}</b> (<PublisherCountry />). “Personal data” contact
      details: <LegalValue value={legal.privacyEmail} />. Otherwise, use
      the <Link to="/contact" className="text-emerald-600 underline">Contact page</Link>.
    </P>

    <H2 id="donnees">Data processed</H2>
    <H3>Anonymous users</H3>
    <P>
      No account is required. Technical metadata strictly necessary to protect the service and to make a
      share work is processed (see below). Shared content is encrypted in the browser.
    </P>
    <H3>Users with an account</H3>
    <UL>
      <li>Account data: identity, email, company, subscription data.</li>
      <li>Usage data related to the workspace (sharing history, members).</li>
    </UL>
    <H3>Technical metadata</H3>
    <UL>
      <li>Creation date, expiration and status of a share.</li>
      <li>Pseudonymised anti-abuse information, kept for a short period.</li>
      <li>
        When the contact form is used, technical information such as the IP address may be recorded for
        security and abuse prevention. Retention period to be defined before commercial production.
      </li>
    </UL>
    <H3>Encrypted content</H3>
    <P>
      The content of secrets and responses is processed in encrypted form. SecurePass does not receive the
      decryption key and therefore cannot read this content.
    </P>

    <H2 id="finalites">Purposes</H2>
    <UL>
      <li>Providing the sharing and information request service.</li>
      <li>Ensuring security and preventing abuse.</li>
      <li>Managing accounts, workspaces and subscriptions.</li>
    </UL>

    <H2 id="destinataires">Recipients</H2>
    <P>
      The data is intended for {legal.publisherName} and, where applicable, its technical providers
      strictly necessary for the operation of the service (see the
      <Link to="/sous-traitants" className="text-emerald-600 underline"> list of subprocessors</Link>).
    </P>

    <H2 id="sous-traitants">Subprocessors</H2>
    <P>
      The list of subprocessors actually in use is published on the dedicated page. No unconfirmed
      provider is listed.
    </P>

    <H2 id="conservation">Retention</H2>
    <P>
      Retention periods are listed on the
      <Link to="/securite" className="text-emerald-600 underline"> Security & Trust Center</Link> page.
      For any period not yet set in the commercial framework: <LegalValue value={null} />.
    </P>

    <H2 id="securite">Security</H2>
    <P>
      Browser-side encryption, key kept separate from the content, one-time read, automatic expiration and
      HTTP hardening measures. See the Security page for details.
    </P>

    <H2 id="test-mdp">Password test</H2>
    <P>
      The “Test a password” page offers a local strength analysis and an optional exposure check in Pwned
      Passwords (Have I Been Pwned). For this feature:
    </P>
    <UL>
      <li>the password is never sent;</li>
      <li>the full SHA-1 hash is never sent;</li>
      <li>only the 5-character prefix of this hash is transmitted;</li>
      <li>the request is only triggered by a user action;</li>
      <li>Have I Been Pwned may receive the usual network information of the connection, in particular the public IP address.</li>
    </UL>

    <H2 id="droits">Your rights</H2>
    <P>
      Depending on the applicable regulations, you may have rights of access, rectification, erasure,
      objection and restriction.
    </P>

    <H2 id="exercice">Exercising your rights</H2>
    <P>
      Send your request to <LegalValue value={legal.privacyEmail} /> or via the
      <Link to="/contact" className="text-emerald-600 underline"> Contact page</Link>.
    </P>

    <H2 id="transferts">Possible transfers</H2>
    <P>
      Information about any data transfers will be specified once the list of subprocessors and the
      hosting location have been confirmed: <LegalValue value={null} />.
    </P>

    <H2 id="maj">Policy updates</H2>
    <P>
      This policy may change. The version and update date are shown at the top of the page.
    </P>

    <Notice testid="privacy-work-note">
      Working version to be legally validated before commercial production. Items marked “to be
      completed” are not invented.
    </Notice>
  </Page>
);

export default PrivacyPage;
